/**
 * Owner disbursements. Two sections: a period run (computed live — there is
 * no persisted draft, see the service's "no pg-boss job" reasoning) and the
 * list of already-approved disbursements. Approval is per-payout, every
 * time — there is exactly one Approve button per owner row below, never a
 * "select all", per CLAUDE.md's explicit "no approve-all button, now or
 * later."
 *
 * Connect (Transfer/Payout) is real as of this pass: an owner shown
 * `willSettleViaConnect` will actually move money through Stripe on
 * approval; one who isn't ENROLLED stays APPROVED, reachable only by
 * external settlement, exactly as before Connect existed.
 */

import { Fragment, useEffect, useState } from 'react';
import { useInvalidate, useList } from '@refinedev/core';
import type { OwnerDisbursementDto, OwnerDisbursementRunPreviewDto } from 'shared';
import { api } from '../../../lib/api.js';
import { useCan } from '../../../lib/capabilities.js';
import { Field, FormErrors, TextInput, humanizeEnum } from '../../../components/Form.js';
import { DataTable, type Column } from '../../../components/DataTable.js';

/** The calendar month in progress right now — the default period this
 *  screen opens to. Eligibility (SETTLED + FUNDS_HOLD_DAYS elapsed + not
 *  yet claimed) isn't scoped to the period being computed, so running the
 *  current, still-open month is a safe, read-only preview: nothing in it
 *  is approved automatically, and a payment not yet eligible simply
 *  doesn't show up until it is. Staff still pick a different period via
 *  the date fields below whenever they actually mean to run a closed one. */
function currentMonth(): { periodStart: string; periodEnd: string } {
  const now = new Date();
  const firstOfThisMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const firstOfNextMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  const lastOfThisMonth = new Date(firstOfNextMonth.getTime() - 24 * 60 * 60 * 1000);
  return {
    periodStart: firstOfThisMonth.toISOString().slice(0, 10),
    periodEnd: lastOfThisMonth.toISOString().slice(0, 10),
  };
}

function RunSection() {
  const canApprove = useCan('owner-disbursements:approve');
  const [{ periodStart, periodEnd }, setPeriod] = useState(currentMonth);
  const [preview, setPreview] = useState<OwnerDisbursementRunPreviewDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [approvingOwnerId, setApprovingOwnerId] = useState<string | null>(null);
  const [expandedOwnerId, setExpandedOwnerId] = useState<string | null>(null);
  // Approve stays disabled until its owner's box is checked — a deliberate
  // second step against an accidental click, since approving fires a real
  // Stripe Transfer for a Connect-enrolled owner. Reset on every fresh
  // preview load (below) so a box checked against one period's numbers can
  // never carry over and silently pre-arm the button for a different
  // period's numbers.
  const [confirmedOwnerIds, setConfirmedOwnerIds] = useState<Set<string>>(new Set());
  const invalidate = useInvalidate();

  async function loadPreview() {
    setLoading(true);
    setError(null);
    try {
      const result = await api<OwnerDisbursementRunPreviewDto>(
        `/owner-disbursements/preview?periodStart=${periodStart}&periodEnd=${periodEnd}`,
      );
      setPreview(result);
      setConfirmedOwnerIds(new Set());
    } catch (caught) {
      setError(caught);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }

  // Auto-run once, for whatever period the screen opened to — staff land
  // here already looking at numbers rather than an empty form. Deliberately
  // an empty dependency array: this fires on mount only, not on every
  // periodStart/periodEnd edit — the explicit Run button is what fires
  // those, same as before.
  useEffect(() => {
    void loadPreview();
  }, []);

  async function approve(ownerId: string) {
    setApprovingOwnerId(ownerId);
    setError(null);
    try {
      await api<OwnerDisbursementDto>('/owner-disbursements', {
        method: 'POST',
        body: JSON.stringify({ ownerId, periodStart, periodEnd }),
      });
      // loadPreview() below resets confirmedOwnerIds entirely, so the
      // just-approved owner's checkbox is cleared along with everyone else's.
      await loadPreview();
      await invalidate({ resource: 'owner-disbursements', invalidates: ['list'] });
    } catch (caught) {
      setError(caught);
    } finally {
      setApprovingOwnerId(null);
    }
  }

  return (
    <div className="card stack">
      <h2>Run a period</h2>
      <p className="muted">
        Computed live from the ledger every time — nothing here is persisted until you approve a
        specific owner's disbursement.
      </p>

      <div className="row">
        <Field label="Period start" name="periodStart">
          <TextInput
            name="periodStart"
            type="date"
            value={periodStart}
            onChange={(value) => setPeriod((p) => ({ ...p, periodStart: value }))}
          />
        </Field>
        <Field label="Period end" name="periodEnd">
          <TextInput
            name="periodEnd"
            type="date"
            value={periodEnd}
            onChange={(value) => setPeriod((p) => ({ ...p, periodEnd: value }))}
          />
        </Field>
        <button
          type="button"
          className="button"
          disabled={loading}
          onClick={() => void loadPreview()}
        >
          {loading ? 'Loading…' : 'Run'}
        </button>
      </div>

      {error ? <FormErrors error={error} /> : null}

      {preview ? (
        <>
          {preview.platformBalance && !preview.platformBalance.sufficient ? (
            <div className="alert alert--error stack">
              <strong>Platform balance is short — do not approve until this clears.</strong>
              <p className="bento__hint">
                ${preview.platformBalance.availableAmount} available, $
                {preview.platformBalance.neededAmount} needed for the Connect-enrolled owners in
                this run. Approving now would fire Transfers that fail partway through the batch.
              </p>
            </div>
          ) : null}

          {preview.flaggedProperties.length > 0 ? (
            <div className="alert alert--error stack">
              <strong>Flagged properties — excluded from this run, review before proceeding</strong>
              <ul>
                {preview.flaggedProperties.map((f) => (
                  <li key={f.propertyId}>
                    {f.propertyName}: {f.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {preview.owners.length === 0 ? (
            <p className="muted">Nothing to disburse for this period.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Owner</th>
                  <th className="num">Net this period</th>
                  <th className="num">Payable</th>
                  <th className="num">Balance after</th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {preview.owners.map((owner) => (
                  <Fragment key={owner.ownerId}>
                    <tr>
                      <td>{owner.ownerName}</td>
                      <td className="num">${owner.netAmount}</td>
                      <td className="num">${owner.amount}</td>
                      <td className="num">${owner.balanceAfter}</td>
                      <td>
                        {owner.alreadyApproved ? (
                          <span className="pill">already approved</span>
                        ) : null}
                        {owner.roundingRemainderApplied ? (
                          <span className="pill">rounding remainder</span>
                        ) : null}
                        {owner.willSettleViaConnect ? (
                          <span
                            className="pill pill--positive"
                            title="Approving will Transfer and Payout via Stripe Connect"
                          >
                            via Connect
                          </span>
                        ) : (
                          <span
                            className="pill"
                            title="Not enrolled — approving leaves this APPROVED, pending external settlement"
                          >
                            external settlement
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="stack">
                          <button
                            type="button"
                            className="button button--quiet"
                            onClick={() =>
                              setExpandedOwnerId((id) =>
                                id === owner.ownerId ? null : owner.ownerId,
                              )
                            }
                          >
                            {expandedOwnerId === owner.ownerId ? 'Hide lines' : 'Show lines'}
                          </button>
                          {canApprove && !owner.alreadyApproved ? (
                            <span className="row" style={{ gap: '0.4rem' }}>
                              <label className="row" style={{ gap: '0.25rem' }}>
                                <input
                                  type="checkbox"
                                  checked={confirmedOwnerIds.has(owner.ownerId)}
                                  onChange={(event) => {
                                    setConfirmedOwnerIds((previous) => {
                                      const next = new Set(previous);
                                      if (event.target.checked) next.add(owner.ownerId);
                                      else next.delete(owner.ownerId);
                                      return next;
                                    });
                                  }}
                                />
                                <span className="muted" style={{ fontSize: '0.8rem' }}>
                                  Confirm
                                </span>
                              </label>
                              <button
                                type="button"
                                className="button"
                                disabled={
                                  approvingOwnerId === owner.ownerId ||
                                  !confirmedOwnerIds.has(owner.ownerId)
                                }
                                onClick={() => void approve(owner.ownerId)}
                              >
                                {approvingOwnerId === owner.ownerId ? 'Approving…' : 'Approve'}
                              </button>
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                    {expandedOwnerId === owner.ownerId ? (
                      <tr key={`${owner.ownerId}-lines`}>
                        <td colSpan={6}>
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th>Property</th>
                                <th>Description</th>
                                <th className="num">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {owner.lines.map((line, index) => (
                                <tr key={`${line.paymentId ?? 'fee'}-${index}`}>
                                  <td>{humanizeEnum(line.lineType)}</td>
                                  <td>{line.propertyName ?? '—'}</td>
                                  <td>{line.description}</td>
                                  <td className="num">${line.amount}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : null}
    </div>
  );
}

function SettleExternallyForm({
  disbursementId,
  ownerName,
  amount,
  onDone,
}: {
  disbursementId: string;
  ownerName: string;
  amount: string;
  onDone: () => void;
}) {
  const [referenceNumber, setReferenceNumber] = useState('');
  const [settledOn, setSettledOn] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit() {
    const reference = referenceNumber.trim();
    if (!reference) return;
    // Stripe never sees or verifies this record — it's a manual "we already
    // received a check/wire outside the app" entry. No system check catches
    // a typo or a double-click here, so this is the one confirmation this
    // screen forces, matching the confirm-dialog convention already used
    // elsewhere in this app (DocumentList, VendorForm, LeaseShow) rather
    // than inventing a new one.
    const confirmed = window.confirm(
      `Record a $${amount} external settlement for ${ownerName}, reference "${reference}", dated ${settledOn}?\n\n` +
        'This is a manual record — Stripe does not verify it, and it cannot be undone through the app.',
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/owner-disbursements/${disbursementId}/settle-externally`, {
        method: 'POST',
        body: JSON.stringify({ referenceNumber: reference, settledOn }),
      });
      onDone();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row">
      <input
        className="field__input"
        placeholder="Check / wire reference"
        value={referenceNumber}
        onChange={(event) => setReferenceNumber(event.target.value)}
      />
      <input
        className="field__input"
        type="date"
        value={settledOn}
        onChange={(event) => setSettledOn(event.target.value)}
      />
      <button
        type="button"
        className="button button--quiet"
        disabled={busy || !referenceNumber.trim()}
        onClick={() => void submit()}
      >
        {busy ? 'Recording…' : 'Record settlement'}
      </button>
      {error ? <FormErrors error={error} /> : null}
    </div>
  );
}

function RetryPayoutButton({
  disbursementId,
  onDone,
}: {
  disbursementId: string;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function retry() {
    setBusy(true);
    setError(null);
    try {
      await api(`/owner-disbursements/${disbursementId}/retry-payout`, { method: 'POST' });
      onDone();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: '0.2rem' }}>
      <button
        type="button"
        className="button button--quiet"
        disabled={busy}
        onClick={() => void retry()}
      >
        {busy ? 'Retrying…' : 'Retry payout'}
      </button>
      {error ? <FormErrors error={error} /> : null}
    </div>
  );
}

function ListSection() {
  const canApprove = useCan('owner-disbursements:approve');
  const invalidate = useInvalidate();
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading, error } = useList<OwnerDisbursementDto>({
    resource: 'owner-disbursements',
    pagination: { mode: 'off' },
  });

  function refresh() {
    setOpenId(null);
    void invalidate({ resource: 'owner-disbursements', invalidates: ['list'] });
  }

  const columns: Column<OwnerDisbursementDto>[] = [
    { key: 'owner', header: 'Owner', render: (row) => row.ownerName },
    { key: 'period', header: 'Period', render: (row) => `${row.periodStart} – ${row.periodEnd}` },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="stack" style={{ gap: '0.15rem' }}>
          <span
            className={`pill${row.status === 'PAYOUT_FAILED' ? ' pill--negative' : row.status === 'PAID' ? ' pill--positive' : ''}`}
          >
            {humanizeEnum(row.status)}
          </span>
          {row.transferredAt ? (
            <span className="muted" style={{ fontSize: '0.75rem' }}>
              Transferred {row.transferredAt.slice(0, 10)}
            </span>
          ) : null}
          {row.paidAt ? (
            <span className="muted" style={{ fontSize: '0.75rem' }}>
              Paid {row.paidAt.slice(0, 10)}
            </span>
          ) : null}
          {row.payoutFailureReason ? (
            <span className="muted" style={{ fontSize: '0.75rem' }}>
              {row.payoutFailureReason}
              {row.payoutAttemptCount > 0 ? ` (attempt ${String(row.payoutAttemptCount)})` : ''}
            </span>
          ) : null}
        </div>
      ),
    },
    { key: 'amount', header: 'Amount', numeric: true, render: (row) => `$${row.amount}` },
    {
      key: 'actions',
      header: '',
      render: (row) => {
        if (!canApprove) return null;
        if (row.status === 'PAYOUT_FAILED') {
          return <RetryPayoutButton disbursementId={row.id} onDone={refresh} />;
        }
        if (row.status === 'APPROVED') {
          return openId === row.id ? (
            <SettleExternallyForm
              disbursementId={row.id}
              ownerName={row.ownerName}
              amount={row.amount}
              onDone={refresh}
            />
          ) : (
            <button
              type="button"
              className="button button--quiet"
              onClick={() => setOpenId(row.id)}
            >
              Record external settlement
            </button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div className="card stack">
      <h2>All disbursements</h2>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error}
        emptyMessage="No disbursements approved yet."
      />
    </div>
  );
}

export function OwnerDisbursementRunPage() {
  return (
    <section className="stack">
      <h1>Owner Disbursements</h1>
      <p className="muted">
        Approving an enrolled owner triggers a real Stripe Transfer then Payout. An owner not
        enrolled in Connect still displays here, but payable only by a staff-recorded external
        settlement.
      </p>
      <RunSection />
      <ListSection />
    </section>
  );
}
